import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { useAuth } from '../../providers/AuthProvider';
import { StatusMessage } from '../../components/ui/Feedback';
import { SignatureSection } from '../../components/account/SignatureSection';

/** Minimum new-password length — mirrors `passwordSchema` (@propmgt/shared); the server re-enforces. */
const MIN_PASSWORD = 12;

/** The controller the pure view renders — the container supplies real handlers; tests stub it. */
export interface AccountController {
  current: string;
  next: string;
  confirm: string;
  busy: boolean;
  error: string | null;
  done: boolean;
  onCurrent: (v: string) => void;
  onNext: (v: string) => void;
  onConfirm: (v: string) => void;
  onSubmit: () => void;
}

/**
 * Pure, presentational Account screen (change password). Rendered inside the AdminLayout <main>
 * landmark, so it owns the single <h1>, labelled controls, and aria-live status. Isolated enough
 * to pass axe on its own (see test/a11y.test.tsx).
 */
export function AccountView({ c }: { c: AccountController }) {
  return (
    <>
      <h1>Account</h1>
      <p className="subtitle">Manage your sign-in credentials.</p>

      <div className="card" style={{ maxWidth: 480 }}>
        <h2 style={{ marginTop: 0 }}>Change password</h2>
        <p className="muted">
          Use at least {MIN_PASSWORD} characters. Changing your password signs out every other
          session.
        </p>

        {c.error && <StatusMessage tone="err">{c.error}</StatusMessage>}
        {c.done && <StatusMessage tone="ok">Your password has been changed.</StatusMessage>}

        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            c.onSubmit();
          }}
        >
          <div>
            <label htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={c.current}
              onChange={(e) => c.onCurrent(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={c.next}
              onChange={(e) => c.onNext(e.target.value)}
            />
            <p className="field-hint">At least {MIN_PASSWORD} characters.</p>
          </div>
          <div>
            <label htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={c.confirm}
              onChange={(e) => c.onConfirm(e.target.value)}
            />
          </div>
          <button type="submit" disabled={c.busy}>
            {c.busy ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </div>
    </>
  );
}

/** Container: drives the change-password flow, then refreshes the session (it rotates on success). */
export function AdminAccount() {
  const { refresh, principal } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async () => {
    setError(null);
    setDone(false);
    if (next.length < MIN_PASSWORD) {
      setError(`New password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setError('New password must be different from your current password.');
      return;
    }
    setBusy(true);
    try {
      await api.auth.changePassword({ currentPassword: current, newPassword: next });
      // The change revokes ALL sessions and mints a fresh one for this client; re-read the session
      // so the client + provider adopt the NEW CSRF token (the old one is now dead).
      await refresh();
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AccountView
        c={{
          current,
          next,
          confirm,
          busy,
          error,
          done,
          onCurrent: setCurrent,
          onNext: setNext,
          onConfirm: setConfirm,
          onSubmit,
        }}
      />
      {/* 0.27.0 — the owner's signature (typed name/title + handwritten image). Owner-only: the API refuses staff. */}
      {principal?.role === 'owner' && <SignatureSection />}
    </>
  );
}
